/**
 * Financial arithmetic + display-formatting utilities.
 *
 * The ADR 060 — Money Type library (`Money`, `toWire` / `fromWire`, `format`,
 * `minorUnits`, the rounding modes): an exact Decimal paired with its currency.
 * No number helper is left: BL-2008 deleted `addMoney` / `subMoney` /
 * `mulMoney`, and BL-2455 deleted `roundMoney`, each with its last caller.
 *
 * Rules
 * -----
 * • Never use native `*`, `/`, `+`, `-` on monetary values — use `Money`.
 * • Always pass the tenant/auction currency to `formatCurrency` — never
 *   default to USD at the call site. The utility's USD fallback is only a
 *   safety net for the brief window when a new column isn't yet populated.
 */
export {
    CURRENCY_LABELS,
    SUPPORTED_CURRENCIES,
    currencyPluralName,
    isSupportedCurrency,
    minorUnits,
    type SupportedCurrency,
} from './currency';
export {
    format,
    formatCurrency,
    formatDecimal,
    moneyInputProps,
    type FormatCurrencyOptions,
    type FormatMoneyOptions,
    type MoneyInputProps,
    type NegativeStyle,
} from './format';
export { CurrencyMismatchError, Money } from './money';
export { exceedsMoneyCeiling, fitsMoneyColumn, largestStorableMoney, moneyCeiling } from './ceiling';
export {
    formatMoneyText,
    formatScheduleAmount,
    MONEY_EM_DASH,
    toDisplayMoney,
    type ScheduleCurrencyOption,
} from './display-text';
export { fromWire, toWire, type MoneyWire } from './codec';
export { HALF_EVEN, HALF_UP, type RoundingMode } from './rounding';
export { isWholeMinorUnits, minorUnitStep } from './whole-minor-units';
export { toMinorUnits } from './minor-units-amount';
export { groupThousands, groupTypedMoney } from './group-thousands';
export {
    MONEY_INPUT_MALFORMED_MESSAGE,
    MONEY_INPUT_NEGATIVE_MESSAGE,
    isTypedDecimal,
    normalizeTypedText,
    outOfRangeMessage,
    parseMoneyInput,
    tooManyDecimalsMessage,
    type MoneyInputCode,
    type MoneyInputResult,
} from './parse-money-input';
export { storedMoney } from './stored-money';
export {
    isWithheldMoney,
    storedMoneyOrWithheld,
    toWireOrWithheld,
    WITHHELD_AMOUNT_LABEL,
    withheldMoney,
    type MoneyOrWithheldWire,
    type WithheldMoneyWire,
} from './withheld';

